import React from "react";
import { createRoot } from "react-dom/client";
import root from "react-shadow";
import { AuthProvider } from "../shared/providers/AuthProvider";
import { TenantProvider } from "../shared/providers/TenantProvider";
import { ApiProvider } from "../shared/providers/ApiProvider";
import { ToastProvider } from "../shared/ui/hooks/use-toast";
import { DocumentLinkProvider } from "./providers/DocumentLinkContext";
import { LinkType } from "./types";
import DocumentManagerApp from "./DocumentManagerApp";
import PublicDocumentListApp from "./PublicDocumentListApp";
import metronicStyles from "../shared/ui/styles/styles.css";
import { library } from "@fortawesome/fontawesome-svg-core";
import { fas } from "@fortawesome/free-solid-svg-icons";

library.add(fas);

interface DocumentManagerModuleProps {
  linkType: string;
  linkTargetId: number;
  customerId: number;
}

const DocumentManagerModule: React.FC<DocumentManagerModuleProps> = ({
  linkType,
  linkTargetId,
  customerId
}) => {
  return (
    <AuthProvider>
      <TenantProvider>
        <ApiProvider>
          <DocumentLinkProvider
            linkType={linkType}
            linkTargetId={linkTargetId}
            customerId={customerId}
          >
            <root.div className="document-manager">
              <style>{metronicStyles}</style>
              <ToastProvider>
                <DocumentManagerApp />
              </ToastProvider>
            </root.div>
          </DocumentLinkProvider>
        </ApiProvider>
      </TenantProvider>
    </AuthProvider>
  );
};

declare global {
  interface Window {
    renderDocumentManager: () => void;
    renderPublicDocumentList?: () => void;
    documentManagerLoaded?: boolean;
  }
}

window.renderDocumentManager = function renderDocumentManager(): void {
  const el = document.getElementById("document-manager-container");
  if (!el) return;

  const linkType = el.dataset.linkType ?? LinkType.Project;
  const linkTargetId = parseInt(
    el.dataset.linkTargetId ?? el.dataset.projectId ?? "0",
    10
  );
  const customerId = parseInt(el.dataset.customerId ?? "0", 10);

  if (!linkTargetId) {
    console.error("Document Manager: linkTargetId (or projectId) is required");
    return;
  }

  const reactRoot = createRoot(el);
  reactRoot.render(
    <DocumentManagerModule
      linkType={linkType}
      linkTargetId={linkTargetId}
      customerId={customerId}
    />
  );
};

function renderPublicDocumentList(): void {
  try {
    const container = document.getElementById("public-document-list-container");
    if (!container) return;

    const linkTargetId = parseInt(
      container.dataset.linkTargetId ?? container.dataset.projectId ?? "0",
      10
    );
    if (!linkTargetId) {
      console.error(
        "PublicDocumentList: Missing data-link-target-id or data-project-id attribute"
      );
      return;
    }

    const linkType = container.dataset.linkType ?? LinkType.Project;

    const reactRoot = createRoot(container);
    reactRoot.render(
      <DocumentLinkProvider
        linkType={linkType}
        linkTargetId={linkTargetId}
        customerId={0}
      >
        <PublicDocumentListApp />
      </DocumentLinkProvider>
    );
  } catch (error) {
    console.error("PublicDocumentList: Error during initialization", error);
  }
}

window.renderPublicDocumentList = renderPublicDocumentList;

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", renderPublicDocumentList);
} else {
  renderPublicDocumentList();
}

export default DocumentManagerModule;
